import React from "react";
import SVG from "react-inlinesvg";
import { Modal } from "react-bootstrap";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import cogoToast from "cogo-toast";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { Form as FormLayout } from "react-bootstrap";
import TextInput from "../../../Common/components/text-input";
import { useCreateSecurityQuestionMutation, useGetUserGroupsQuery } from "../../../../../redux/apiSlice";

const normalizeUserGroups = (value) => {
    const list = Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [];
    return list.map((group) => ({
        value: group.groupId ?? group.id ?? group.key ?? group.name,
        label: group.name ?? group.groupId ?? group.id ?? group.key,
    })).filter((group) => group.value);
};

const CreateSecurityQuestionModal = ({ showCreateSecurityQuestionModal, onHide, userGroup, tableRef }) => {
    const [createSecurityQuestion, { isLoading }] = useCreateSecurityQuestionMutation();
    const { data: userGroupsData } = useGetUserGroupsQuery();
    const userGroups = normalizeUserGroups(userGroupsData);

    const validationSchema = Yup.object({
        question: Yup.string()
            .required('Security question is required')
            .min(10, 'Question must be at least 10 characters')
            .max(200, 'Question must not exceed 200 characters'),
        userGroup: Yup.string().required('User group is required'),
    });

    return (
        <Modal
            show={showCreateSecurityQuestionModal}
            onHide={onHide}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={isLoading ? "static" : true}
            keyboard={!isLoading}
        >
            <Formik
                enableReinitialize
                initialValues={{
                    question: "",
                    userGroup: userGroup || "trove"
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                    const payload = {
                        question: values.question.trim(),
                        userGroup: values.userGroup
                    };
                    createSecurityQuestion(payload)
                        .unwrap()
                        .then(() => {
                            cogoToast.success("Security question created successfully");
                            if (tableRef?.current?.refresh) {
                                tableRef.current.refresh();
                            }
                            onHide();
                        })
                        .catch((error) => {
                            cogoToast.error(
                                error?.data?.message || "Failed to create security question"
                            );
                        });
                }}
            >
                {({ isValid, values, handleChange, dirty }) => (
                    <Form noValidate>
                        <Modal.Header>
                            <Modal.Title id="contained-modal-title-vcenter">
                                <span className="svg-icon svg-icon-lg svg-icon-primary mr-3">
                                    <SVG
                                        title=""
                                        src={toAbsoluteUrl("/media/svg/icons/Shopping/Safe.svg")}
                                    />
                                </span>
                                Create Security Question
                            </Modal.Title>
                            <button type="button" className="close" onClick={onHide}>
                                <span aria-hidden="true" className="d-block">×</span>
                                <span className="sr-only">Close</span>
                            </button>
                        </Modal.Header>
                        
                        <Modal.Body className="px-10 py-5">
                            <FormLayout.Row>
                                <div style={{ width: "100%" }}>
                                    <FormLayout.Group>
                                        <FormLayout.Label>User Group</FormLayout.Label>
                                        <FormLayout.Control
                                            as="select"
                                            name="userGroup"
                                            value={values.userGroup}
                                            onChange={handleChange}
                                        >
                                            {userGroups.length === 0 && (
                                                <option value={values.userGroup}>{values.userGroup}</option>
                                            )}
                                            {userGroups.map((group) => (
                                                <option key={group.value} value={group.value}>
                                                    {group.label}
                                                </option>
                                            ))}
                                        </FormLayout.Control>
                                        <small className="form-text text-muted">
                                            Which user group this question will be asked to.
                                        </small>
                                    </FormLayout.Group>
                                </div>
                            </FormLayout.Row>
                            <FormLayout.Row>
                                <div style={{ width: "100%" }}>
                                    <TextInput
                                        label="Security Question"
                                        name="question"
                                        placeholder="e.g., What was the name of your first pet?"
                                        onChange={handleChange}
                                        as="textarea"
                                        rows={3}
                                    />
                                    <small className="form-text text-muted">
                                    Enter a security question that users will be required to answer
                                    </small>
                                </div>
                            </FormLayout.Row>
                        </Modal.Body>

                        <Modal.Footer>
                            <button
                                type="button"
                                onClick={onHide}
                                className="btn btn-light btn-elevate px-4"
                                disabled={isLoading}
                            >
                                Close
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary btn-elevate px-4 ml-4"
                                disabled={isLoading || !isValid || !dirty}
                            >
                                {isLoading ? "Creating..." : "Create"}
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default CreateSecurityQuestionModal;